import React, { useState } from 'react';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
// Updated to use correct API URL via GitHub secrets

interface PasswordResetProps {
  onBack: () => void;
  initialEmail?: string;
}

const PasswordReset: React.FC<PasswordResetProps> = ({ onBack, initialEmail = '' }) => {
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [formData, setFormData] = useState({
    email: initialEmail,
    verificationCode: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const { forgotPassword, confirmPassword } = useCognitoAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await forgotPassword(formData.email);
      setSuccess('Reset code sent! Check your email for the verification code.');
      setStep('confirm');
    } catch (error: any) {
      console.error('Password reset request error:', error);
      if (error.message?.includes('UserNotFoundException')) {
        setError('No account found with that email address.');
      } else if (error.message?.includes('LimitExceededException')) {
        setError('Too many requests. Please wait before trying again.');
      } else {
        setError(error.message || 'Failed to send reset code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await confirmPassword(formData.email, formData.verificationCode, formData.newPassword);
      setSuccess('Password reset successfully! You can now sign in with your new password.');
      setTimeout(() => {
        onBack();
      }, 2000);
    } catch (error: any) {
      console.error('Password confirmation error:', error);
      if (error.message?.includes('CodeMismatchException')) {
        setError('Invalid verification code. Please check your code and try again.');
      } else if (error.message?.includes('ExpiredCodeException')) {
        setError('Verification code has expired. Please request a new one.');
      } else if (error.message?.includes('InvalidPasswordException')) {
        setError('Password does not meet requirements. Please use a stronger password.');
      } else {
        setError(error.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === 'request') {
    return (
      <div className="login-container">
        <div className="login-form">
          <h2>Reset Your Password</h2>
          <p>Enter your email address and we'll send you a verification code to reset your password.</p>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <form onSubmit={handleRequestReset}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Enter your email address"
              />
            </div>

            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
          </form>

          <button
            type="button"
            onClick={onBack}
            className="back-button"
            disabled={loading}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-form">
        <h2>Enter New Password</h2>
        <p>Enter the verification code sent to your email and your new password.</p>
        <p className="info-text">Email: {formData.email}</p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleConfirmReset}>
          <div className="form-group">
            <label htmlFor="verificationCode">Verification Code</label>
            <input
              type="text"
              id="verificationCode"
              name="verificationCode"
              value={formData.verificationCode}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Enter the code from your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Enter your new password"
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Confirm your new password"
              minLength={8}
            />
          </div>

          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="reset-actions">
          <button
            type="button"
            onClick={() => setStep('request')}
            className="text-button"
            disabled={loading}
          >
            Didn't receive code? Send again
          </button>

          <button
            type="button"
            onClick={onBack}
            className="back-button"
            disabled={loading}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;